import { useState, useRef, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { Link, useNavigate } from 'react-router-dom';
import { useSubscriptionCheck } from '@/hooks/useSubscriptionCheck';
import { SubscriptionModal } from '@/components/SubscriptionModal';
import { useAuthProvider } from '@/pages/auth/hooks/useAuthProvider';

import { StoredCookies } from '@/interfaces/auth/cookies.enum';

export default function NavBar() {
	const [cookies] = useCookies([
		StoredCookies.USERNAME,
		StoredCookies.REFRESH_TOKEN,
		StoredCookies.ACCESS_TOKEN,
	]);
	const { handleSignOut } = useAuthProvider();
	const navigate = useNavigate();
	const { isModalOpen, setIsModalOpen, isCheckingSubscription, checkSubscription } = useSubscriptionCheck();
	const connected = !!cookies[StoredCookies.REFRESH_TOKEN] && !!cookies[StoredCookies.USERNAME];
	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
	const userMenuRef = useRef<HTMLDivElement>(null);

	const username = cookies[StoredCookies.USERNAME] || '';
	const userInitial = username.charAt(0).toUpperCase();

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
				setIsUserMenuOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleBulkTickets = () => {
		checkSubscription(() => navigate('/tickets/bulk'));
	};

	return (
		<nav className="bg-white shadow-lg">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex items-center justify-between h-16">
					<div className="flex items-center">
						<div className="hidden md:block ml-10">
							<div className="flex items-center space-x-6">
								<Link
									to="/"
									className="inline-flex items-center text-lg font-semibold text-[#084509] hover:text-[#0F9015] transition-colors"
								>
									Home
								</Link>
								{connected && (
									<>
										<Link
											to="/tickets"
											className="inline-flex items-center text-lg font-semibold text-[#084509] hover:text-[#0F9015] transition-colors"
										>
											My Tickets
										</Link>
										<button
											onClick={handleBulkTickets}
											disabled={isCheckingSubscription}
											className="inline-flex items-center text-lg font-semibold text-[#084509] hover:text-[#0F9015] transition-colors disabled:text-gray-400 bg-transparent p-0"
											data-testid="create-bulk-tickets-button"
										>
											{isCheckingSubscription ? 'Checking...' : 'Upload Tickets'}
										</button>
									</>
								)}
							</div>
						</div>
					</div>
					<div className="hidden md:block">
						<div className="flex items-center space-x-4">
							{!connected ? (
								<>
									<Link
										to="/auth/sign-in"
										className="inline-flex items-center px-6 py-2 text-lg font-semibold text-[#084509] hover:text-[#0F9015] transition-colors"
									>
										Sign In
									</Link>
									<Link
										to="/auth/sign-up"
										className="inline-flex items-center px-6 py-2 text-lg font-semibold text-black bg-[#C9FD05] rounded-lg hover:bg-[#b4e404] transition-colors shadow-md"
									>
										Sign Up
									</Link>
								</>
							) : (
								<div className="relative" ref={userMenuRef}>
									<button
										onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
										className="flex items-center justify-center w-10 h-10 rounded-full bg-[#0F9015] text-white font-bold text-lg hover:bg-[#084509] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C9FD05] focus:ring-offset-2"
										data-testid="user-avatar-button"
										title={username}
									>
										{userInitial}
									</button>
									
									{isUserMenuOpen && (
										<div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
											<div className="px-4 py-2 border-b border-gray-100">
												<p className="text-sm text-gray-500">Signed in as</p>
												<p className="text-sm font-semibold text-[#084509] truncate">{username}</p>
											</div>
											<Link
												to="/settings"
												onClick={() => setIsUserMenuOpen(false)}
												className="block px-4 py-2 text-sm font-semibold text-[#084509] hover:bg-gray-50 hover:text-[#0F9015] transition-colors"
												data-testid="my-account-dropdown-link"
											>
												<span className="flex items-center gap-2">
													<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
													</svg>
													My Account
												</span>
											</Link>
											<button
												onClick={() => {
													setIsUserMenuOpen(false);
													handleSignOut();
												}}
												className="block w-full text-left px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
												data-testid="logout-dropdown-button"
											>
												<span className="flex items-center gap-2">
													<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
													</svg>
													Logout
												</span>
											</button>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			
			<div className="md:hidden">
				<div className="px-2 pt-2 pb-3 space-y-1 bg-gray-50">
					<Link
						to="/"
						className="block px-3 py-2 text-base font-semibold text-[#084509] hover:text-[#0F9015] hover:bg-gray-100 transition-colors"
					>
						Home
					</Link>
					{connected && (
						<>
							{/* User info section for mobile */}
							<div className="flex items-center gap-3 px-3 py-3 border-b border-gray-200 mb-2">
								<div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#0F9015] text-white font-bold text-lg">
									{userInitial}
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm text-gray-500">Signed in as</p>
									<p className="text-sm font-semibold text-[#084509] truncate">{username}</p>
								</div>
							</div>
							
							<Link
								to="/tickets"
								className="block px-3 py-2 text-base font-semibold text-[#084509] hover:text-[#0F9015] hover:bg-gray-100 transition-colors"
							>
								My Tickets
							</Link>
							<button
								onClick={handleBulkTickets}
								disabled={isCheckingSubscription}
								className="block w-full text-left px-3 py-2 text-base font-semibold text-[#084509] hover:text-[#0F9015] hover:bg-gray-100 transition-colors disabled:text-gray-400 bg-transparent"
								data-testid="create-bulk-tickets-button-mobile"
							>
								{isCheckingSubscription ? 'Checking...' : 'Upload Tickets'}
							</button>
							<Link
								to="/settings"
								className="block px-3 py-2 text-base font-semibold text-[#084509] hover:text-[#0F9015] hover:bg-gray-100 transition-colors"
								data-testid="my-account-link-mobile"
							>
								<span className="flex items-center gap-2">
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
									</svg>
									My Account
								</span>
							</Link>
							<div className="px-3 py-2 border-t border-gray-200 mt-2">
								<button
									onClick={handleSignOut}
									className="w-full flex items-center justify-center gap-2 px-4 py-2 text-base font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
									</svg>
									Logout
								</button>
							</div>
						</>
					)}
					{!connected && (
						<div className="px-3 py-2 space-y-2">
							<Link
								to="/auth/sign-in"
								className="block px-4 py-2 text-base font-semibold text-[#084509] hover:text-[#0F9015] transition-colors text-center"
							>
								Sign In
							</Link>
							<Link
								to="/auth/sign-up"
								className="block px-4 py-2 text-base font-semibold text-black bg-[#C9FD05] rounded-lg hover:bg-[#b4e404] transition-colors shadow-md text-center"
							>
								Sign Up
							</Link>
						</div>
					)}
				</div>
			</div>

			<SubscriptionModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				onSubscribed={() => setIsModalOpen(false)}
				price={29.99}
			/>
		</nav>
	);
}
